/**
 * Pure function — classifies the bulk-self-schedule candidate set
 * into eligible / no-email / already-scheduled buckets given the raw
 * `GET /ats_applications/bulk/interview_status` response and the
 * stage action the user picked.
 *
 * Mirrors the categorisation that lived in
 * `KInterviewScheduler/InterviewToSchedule/index.tsx` (the useEffect
 * around lines 64-119) — but lifted out of the component so the
 * wizard can render the same buckets in a different layout and so
 * tests can pin the rules in place.
 *
 * Rules:
 *   1. `invalid_ats_applications[0].ats_applications` — no-email bucket.
 *   2. `selectedAction.existing_interview_applications` — already-
 *      scheduled bucket. Stage-action-specific (the same candidate
 *      may be eligible for action A and ineligible for action B).
 *   3. Everything else in `ats_applications` minus those two ID sets
 *      is eligible.
 *
 * De-duplication is by **contact id** to match the legacy logic —
 * the backend may return the same person twice (different applications
 * on the same job stage) and we only want to ask them to self-schedule
 * once. Eligible IDs are application ids though, not contact ids.
 */
import type {
  BulkSelfScheduleApplication,
  BulkSelfScheduleInterviewStatusResponse,
  BulkSelfScheduleStageAction,
} from './types';

export interface CategoriseResult {
  eligible: BulkSelfScheduleApplication[];
  noEmail: BulkSelfScheduleApplication[];
  alreadyScheduled: BulkSelfScheduleApplication[];
}

export function categoriseApplications(
  status: BulkSelfScheduleInterviewStatusResponse | undefined,
  selectedAction: BulkSelfScheduleStageAction | null
): CategoriseResult {
  if (!status) {
    return { eligible: [], noEmail: [], alreadyScheduled: [] };
  }

  const noEmail = status.invalid_ats_applications?.[0]?.ats_applications ?? [];
  const alreadyScheduled =
    selectedAction?.existing_interview_applications ?? [];

  const invalidContactIds = new Set<number>();
  for (const app of noEmail) invalidContactIds.add(app.contact.id);
  for (const app of alreadyScheduled) invalidContactIds.add(app.contact.id);

  const eligible: BulkSelfScheduleApplication[] = [];
  const seenContacts = new Set<number>();
  for (const app of status.ats_applications ?? []) {
    if (invalidContactIds.has(app.contact.id)) continue;
    // Same contact across multiple applications collapses to the
    // first appearance (mirrors the legacy `filteredContacts` step).
    if (seenContacts.has(app.contact.id)) continue;
    seenContacts.add(app.contact.id);
    eligible.push(app);
  }

  return { eligible, noEmail, alreadyScheduled };
}

/**
 * Auto-advance condition: 1 stage action + every selected candidate
 * eligible. Lets the overlay flip straight to the wizard without
 * making the user click a "Start" button when there's nothing to
 * choose.
 */
export function shouldAutoAdvance(
  status: BulkSelfScheduleInterviewStatusResponse | undefined,
  result: CategoriseResult
): boolean {
  if (!status) return false;
  const onlyOneAction = (status.interview_job_stage_actions ?? []).length === 1;
  const totalSelected = (status.ats_applications ?? []).length;
  return (
    onlyOneAction &&
    totalSelected > 0 &&
    result.eligible.length === totalSelected
  );
}
